import { memo, useCallback, useEffect, useMemo, lazy } from 'react';

import { WebView } from '@zenbusiness/application-commons-webview';
import { useSearchParams } from 'react-router-dom';

import ActivateCardModal from '#client/components/ActivateCardModal';
import AddCounterPartyModal from '#client/components/AddCounterPartyModal';
import ApplicationReceivedModal from '#client/components/ApplicationReceivedModal';
import ApplicationSubmissionModal from '#client/components/ApplicationSubmissionModal';
import AwaitingUploadDocumentsModal from '#client/components/AwaitingUploadDocumentsModal';
import BankingVideoModal from '#client/components/BankingVideoModal';
import EnterEINNumberModal from '#client/components/EnterEINNumberModal';
import DepositMoneyModal from '#client/components/MoveMoney/DepositMoneyModal';
import InstantDepositModal from '#client/components/MoveMoney/InstantDepositModal';
import MoveMoneyChoiceViewModal from '#client/components/MoveMoney/MoveMoneyChoiceViewModal';
import SendMoneyModal from '#client/components/MoveMoney/SendMoneyModal';
import OpenAccountApplicationModal from '#client/components/OpenAccountApplication';
import BoAModal from '#client/components/OpenAccountApplication/components/Steps/ValidationMessage/components/BoAModal';
import OpenDebitCardMailing from '#client/components/OpenDebitCardMailing';
import SetPinModal from '#client/components/SetPinModal';
import { RELEASE_SHOW_INSTANT_DEPOSIT } from '#client/constants/experimentsFlags';
import {
  CHECK_DEPOSIT_ID,
  IDEMPOTENCY_KEY,
  KEY_PARAM_MODAL,
  MODAL_NAMES
} from '#client/constants/modals';
import { MOVE_MONEY_STEPS_WITH_WIRE_TYPE } from '#client/constants/transfer';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useFeatureFlag from '#client/hooks/useFeatureFlag';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { checkIsMobile } from '#client/utils/devices';

const emptyModal = {
  isShow: false,
  forceRedirection: false,
  Component: null,
  props: {}
};

const NOT_FOUND_MODAL = null;

const ResubmitModal = lazy(() => import('#client/components/ResubmitModal'));

function NavigateModals() {
  const { isWebView } = WebView;
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { isEmptyUnitAccountId, unitAccountId } = useUnitAccountId();
  const { isInvalidStatus } = useApplicationStatusContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isShowInstantDepositFlagEnabled, isShowInstantDepositFlagLoading] = useFeatureFlag(
    RELEASE_SHOW_INSTANT_DEPOSIT
  );

  // Display the keys
  const currentModalName = searchParams.get(KEY_PARAM_MODAL);
  const idempotencyKeyFromURL = searchParams.get(IDEMPOTENCY_KEY);

  const handleClose = useCallback(() => {
    if (currentModalName !== NOT_FOUND_MODAL) {
      setSearchParams({}, { replace: true });
    }
  }, [currentModalName, setSearchParams]);

  const handleBackToAppSubmissionModal = useCallback(() => {
    setSearchParams({
      [KEY_PARAM_MODAL]: MODAL_NAMES.APPLICATION_SUBMISSION_MODAL
    });
  }, [setSearchParams]);

  const isDesktop = !checkIsMobile();
  const isWebviewRedirect = isWebView;
  const isShowInstantDepositMoveMoneyModal =
    !isShowInstantDepositFlagLoading && isShowInstantDepositFlagEnabled;

  const modal = useMemo(() => {
    const forceRedirection = isInvalidStatus || isEmptyUnitAccountId;
    switch (currentModalName) {
      case MODAL_NAMES.APPLICATION_INIT_MODAL:
        return {
          isShow: businessEntityUuid,
          forceRedirection: false,
          Component: OpenAccountApplicationModal,
          props: {
            onClose: handleClose
          }
        };
      case MODAL_NAMES.AWAITING_DOCUMENTS:
        return {
          isShow: businessEntityUuid,
          forceRedirection: false,
          Component: AwaitingUploadDocumentsModal,
          props: {
            handleClose,
            handleBack: handleBackToAppSubmissionModal
          }
        };
      case MODAL_NAMES.APPLICATION_SUBMISSION_MODAL:
        return {
          isShow: true,
          forceRedirection: false,
          Component: ApplicationSubmissionModal,
          props: {
            handleClose
          }
        };
      case MODAL_NAMES.DEBIT_CARD_MAILING:
        return {
          isShow: unitAccountId && businessEntityUuid,
          forceRedirection,
          Component: OpenDebitCardMailing,
          props: {
            handleClose
          }
        };
      case MODAL_NAMES.LINK_BANK_ACCOUNT_MODAL:
        return {
          isShow: unitAccountId && businessEntityUuid,
          forceRedirection,
          Component: AddCounterPartyModal,
          props: {
            handleClose,
            mode: AddCounterPartyModal.MODAL_TYPES.ADD_BANK
          }
        };
      case MODAL_NAMES.APPLICATION_RECEIVED_MODAL:
        return {
          isShow: true,
          forceRedirection: false,
          Component: ApplicationReceivedModal,
          props: {
            handleClose
          }
        };
      case MODAL_NAMES.ENTER_EIN_MODAL:
        return {
          isShow: true,
          forceRedirection: false,
          Component: EnterEINNumberModal,
          props: {
            handleClose
          }
        };
      case MODAL_NAMES.ACTIVATE_CARD:
        return {
          isShow: unitAccountId && businessEntityUuid,
          forceRedirection,
          Component: ActivateCardModal,
          props: {
            handleClose
          }
        };
      case MODAL_NAMES.SET_PIN_MODAL:
        return {
          isShow: unitAccountId && businessEntityUuid,
          forceRedirection,
          Component: SetPinModal,
          props: {
            handleClose
          }
        };
      case MODAL_NAMES.MOVE_MONEY_MODAL:
        return {
          isShow: unitAccountId && businessEntityUuid && !isShowInstantDepositFlagLoading,
          forceRedirection,
          Component: MoveMoneyChoiceViewModal,
          props: {
            handleClose,
            actionsKit: {
              resetSearchParameters: handleClose
            }
          }
        };
      case MODAL_NAMES.DEPOSIT_MODAL:
        return {
          isShow: unitAccountId && businessEntityUuid,
          forceRedirection,
          // Open the deposit choice view modal on the mobile device
          Component: isWebView ? MoveMoneyChoiceViewModal : DepositMoneyModal,
          props: {
            handleClose,
            actionsKit: {
              resetSearchParameters: handleClose
            }
          }
        };
      case MODAL_NAMES.BANKING_VIDEO:
        return {
          isShow: true,
          forceRedirection: false,
          Component: BankingVideoModal,
          props: {
            handleClose
          }
        };
      case MODAL_NAMES.RETRY_CHECK_DEPOSITS:
        return {
          isShow: unitAccountId && businessEntityUuid,
          forceRedirection: isDesktop || isWebviewRedirect,
          Component: ResubmitModal,
          props: {
            handleClose,
            checkId: searchParams.get(CHECK_DEPOSIT_ID),
            bypassReason: true
          }
        };
      case MODAL_NAMES.SEND_MONEY:
        return {
          isShow: true,
          forceRedirection: false,
          Component: SendMoneyModal,
          props: {
            handleClose,
            steps: MOVE_MONEY_STEPS_WITH_WIRE_TYPE,
            idempotencyKeyFromURL
          }
        };
      case MODAL_NAMES.DEPOSIT_MONEY:
        return {
          isShow: true,
          forceRedirection,
          Component: DepositMoneyModal,
          props: {
            handleClose
          }
        };
      case MODAL_NAMES.INSTANT_DEPOSIT_MODAL:
        return {
          isShow: unitAccountId && businessEntityUuid && isShowInstantDepositMoveMoneyModal,
          forceRedirection: !isShowInstantDepositMoveMoneyModal,
          Component: InstantDepositModal,
          props: {
            handleClose,
            checkCardErrorsStep: true
          }
        };
      case MODAL_NAMES.BOA_MODAL:
        return {
          isShow: true,
          forceRedirection: false,
          Component: BoAModal,
          props: {
            isOpen: true,
            handleClose
          }
        };
      default:
        return { ...emptyModal, forceRedirection: true };
    }
  }, [
    unitAccountId,
    isInvalidStatus,
    isWebView,
    currentModalName,
    businessEntityUuid,
    isEmptyUnitAccountId,
    idempotencyKeyFromURL,
    isDesktop,
    isWebviewRedirect,
    searchParams,
    handleClose,
    handleBackToAppSubmissionModal,
    isShowInstantDepositFlagLoading,
    isShowInstantDepositMoveMoneyModal
  ]);

  useEffect(() => {
    if (modal.forceRedirection) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.forceRedirection]);

  if (currentModalName === NOT_FOUND_MODAL) {
    return null;
  }

  return modal.isShow && <modal.Component {...modal.props} />;
}

export default memo(NavigateModals);
